import React from "react";
import { NavLink } from "react-router";
import { Hamburger } from "lucide-react";
import { cn } from "@/lib/utils";

export type SidebarMenuItem = {
  label: string;
  key: string;
  icon?: React.ComponentType<{ className?: string }>;
  route: string;
};

type Props = {
  items: SidebarMenuItem[];
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  closeOnNavigate?: boolean;
};

export const SidebarMenuNavLink = React.memo(function SidebarMenuNavLink({
  items,
  sidebarOpen,
  setSidebarOpen,
  closeOnNavigate = true,
}: Props) {
  return (
    <>
      {/* Backdrop para móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "flex h-full flex-col bg-(--card-background) text-muted-foreground border-t border-(--background) transition-all duration-300 ease-in-out z-50",
          // Desktop: sidebar normal
          "lg:relative lg:translate-x-0",
          // Mobile: overlay
          "fixed lg:static inset-y-0 left-0",
          sidebarOpen
            ? "translate-x-0 w-64"
            : "-translate-x-full lg:translate-x-0 lg:w-16",
        )}
      >
        <div className="flex justify-start py-2 px-3.5">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 text-muted-foreground hover:text-primary transition-colors"
            aria-label={sidebarOpen ? "Cerrar menú" : "Abrir menú"}
          >
            <Hamburger className="h-5 w-5 cursor-pointer" />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 pb-4">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.route}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-(--primary) text-(--muted)"
                      : "hover:bg-(--background-muted)",
                  )
                }
                onClick={() => {
                  // Cerrar sidebar en móvil al navegar
                  if (closeOnNavigate && window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
              >
                {Icon ? <Icon className="w-4 h-4 flex-shrink-0" /> : null}
                {sidebarOpen && (
                  <span className="whitespace-nowrap">{item.label}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
});
